import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFrameworkMethodsPropType } from 'ui-components/prop-types/modal-framework-methods';
import { FloatDatePicker } from 'ui-components/components/float-date-picker';
import { FloatCurrencyInput } from 'ui-components/components/float-currency-input';
import { CurrencySymbolContainer } from '../../../containers/currency-symbol-container';
import { FloatTextArea } from 'ui-components/components/float-text-area';
import { USD } from '../../../constants/currency-codes';

import { DateService } from '../../../services/date-service';
import style from './mark-as-paid-modal.module.less';

const messages = defineMessages({
  paidDate: {
    description: 'paidDate.label',
    defaultMessage: 'Paid Date',
    id: 'SqR/Sy',
  },
  totalAmountPaid: {
    description: 'totalAmountPaid.label',
    defaultMessage: 'Total Paid',
    id: 'jUOUWj',
  },
  shippingAmountPaid: {
    description: 'shippingAmountPaid.label',
    defaultMessage: 'Shipping Paid',
    id: 'L3xkwG',
  },
  internalNote: {
    description: 'internalNote.label',
    defaultMessage: 'Internal Note',
    id: 'FNJGJb',
  },
});

export const MarkAsPaidModal = ({
  markAsPaid,
  modalFrameworkMethods,
  orderTotalCurrencyCode = USD,
  shippingAmountPaidCurrencyCode = USD,
  userDateFormat,
  defaultCurrency = USD,
  paidDateString: paidDateStringProp,
  orderTotal: orderTotalProp = 0,
  shippingAmountPaid: shippingAmountPaidProp = 0,
}) => {
  const intl = useIntl();
  const { closeModal } = modalFrameworkMethods;
  const [paidDateString, setPaidDateString] = useState(paidDateStringProp);
  const [orderTotal, setOrderTotal] = useState(orderTotalProp);
  const [shippingAmountPaid, setShippingAmountPaid] = useState(shippingAmountPaidProp);
  const [internalNote, setInternalNote] = useState('');

  const callMarkAsPaid = useCallback(() => {
    markAsPaid({
      paidDateUtc: paidDateString,
      orderTotal,
      shippingAmountPaid,
      internalNote,
      orderTotalCurrencyCode,
      shippingAmountPaidCurrencyCode,
    });
    closeModal();
  }, [
    internalNote,
    markAsPaid,
    closeModal,
    orderTotal,
    orderTotalCurrencyCode,
    paidDateString,
    shippingAmountPaid,
    shippingAmountPaidCurrencyCode,
  ]);

  return (
    <Modal className={style['mark-as-paid-modal']}>
      <ModalHeader closeModal={closeModal}>
        <FormattedMessage defaultMessage='Mark as Paid' description='markAsPaidModal.header' id='FmJNeD' />
      </ModalHeader>
      <ModalBody className={style['modal-body']}>
        <div className={style['form-field']}>
          <FloatDatePicker
            additionalDateFormats={DateService.getAdditionalDateFormats()}
            allowPastDates
            className={style['paid-date-picker']}
            dateFormat={userDateFormat}
            label={intl.formatMessage(messages.paidDate)}
            onChange={setPaidDateString}
            required
            value={paidDateString}
          />
        </div>
        <div className={style['form-field']}>
          <FloatCurrencyInput
            className={style['total-amount-paid-input']}
            currencySymbol={<CurrencySymbolContainer currencyCode={orderTotalCurrencyCode || defaultCurrency} />}
            label={intl.formatMessage(messages.totalAmountPaid)}
            onChange={setOrderTotal}
            required
            value={orderTotal}
          />
        </div>
        <div className={style['form-field']}>
          <FloatCurrencyInput
            className={style['shipping-amount-paid-input']}
            currencySymbol={<CurrencySymbolContainer currencyCode={shippingAmountPaidCurrencyCode || defaultCurrency} />}
            label={intl.formatMessage(messages.shippingAmountPaid)}
            onChange={setShippingAmountPaid}
            value={shippingAmountPaid}
          />
        </div>
        <div className={style['internal-note-input']}>
          <FloatTextArea label={intl.formatMessage(messages.internalNote)} onChange={setInternalNote} value={internalNote} />
        </div>
      </ModalBody>
      <ModalFooter className={style['modal-footer']}>
        <Button onClick={closeModal} theme='tertiary'>
          <FormattedMessage defaultMessage='Cancel' description='markAsPaidModal.cancel' id='/QfjoS' />
        </Button>
        <Button onClick={callMarkAsPaid} theme='primary'>
          <FormattedMessage defaultMessage='Mark as Paid' description='markAsPaidModal.markAsPaid' id='2rfyCl' />
        </Button>
      </ModalFooter>
    </Modal>
  );
};

MarkAsPaidModal.propTypes = {
  modalFrameworkMethods: ModalFrameworkMethodsPropType.isRequired,
  markAsPaid: PropTypes.func.isRequired,
  userDateFormat: PropTypes.string.isRequired,
  shippingAmountPaid: PropTypes.number,
  orderTotal: PropTypes.number,
  paidDateString: PropTypes.string,
  defaultCurrency: PropTypes.string,
  orderTotalCurrencyCode: PropTypes.string,
  shippingAmountPaidCurrencyCode: PropTypes.string,
};
